import { useState, useRef, useEffect } from 'react';

/**
 * CompactMetric - A small clickable metric for bottom stats bars
 * Uses abbreviated labels to reduce visual complexity
 */

// Simplified metric options with abbreviated labels
const COMPACT_METRICS = [
  { id: 'time', abbr: 'ELAP', label: 'Elapsed Time', formatter: (v) => v },
  { id: 'pm5_time', abbr: 'PM5T', label: 'PM5 Time', formatter: (v) => v },
  { id: 'strokes', abbr: 'ST', label: 'Strokes', formatter: (v) => v },
  { id: 'calories', abbr: 'CAL', label: 'Calories', formatter: (v) => Math.floor(v) },
  { id: 'distance', abbr: 'DIST', label: 'Distance', formatter: (v) => typeof v === 'number' ? Math.floor(v) : v },
  { id: 'watts', abbr: 'W', label: 'Watts', formatter: (v) => v },
  { id: 'stroke_rate', abbr: 'SPM', label: 'Stroke Rate', formatter: (v) => v },
  { id: 'heart_rate', abbr: 'HR', label: 'Heart Rate', formatter: (v) => v },
  { id: 'split', abbr: 'SPL', label: 'Split/500m', formatter: (v) => v },
  { id: 'avg_split_3', abbr: 'AVG3', label: 'Avg Split/3', formatter: (v) => v },
  { id: 'meters_per_second', abbr: 'M/S', label: 'Speed', formatter: (v) => typeof v === 'number' ? v.toFixed(1) : v },
  { id: 'projected_finish', abbr: 'PROJ', label: 'Projected Finish', formatter: (v) => v },
  { id: 'zone', abbr: 'ZONE', label: 'HR Zone', formatter: (v) => v },
  { id: 'avg_hr', abbr: 'AVG', label: 'Avg HR', formatter: (v) => v }
];

export default function CompactMetric({
  metricId = 'time',
  allMetrics = {},
  position = 0,
  onChangeMetric,
  storageKey = 'bottom_metrics',
  compact = false
}) {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  
  const metric = COMPACT_METRICS.find(m => m.id === (metricId === 'stroke_watts' ? 'watts' : metricId)) || COMPACT_METRICS[0];
  const value = metric.formatter(allMetrics[metricId] ?? 0);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);
  
  const handleSelect = (newMetricId) => {
    setShowDropdown(false);
    if (onChangeMetric) {
      onChangeMetric(position, newMetricId);
    }
  };
  
  return (
    <div className="relative flex-1 text-center px-3" ref={dropdownRef}>
      <div 
        onClick={() => setShowDropdown(!showDropdown)}
        className="cursor-pointer hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-lg py-1 -mx-1 px-1 transition-colors"
        data-testid={`compact-metric-${position}`}
      >
        <div className={`font-bold text-zinc-400 dark:text-zinc-500 ${compact ? 'text-[10px] mb-0.5' : 'text-xs mb-1'}`}>{metric.abbr}</div>
        <div className={`font-bold text-zinc-700 dark:text-white ${compact ? 'text-base landscape:text-lg' : 'text-lg'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {value}
        </div>
      </div>
      
      {showDropdown && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-lg shadow-lg z-50 min-w-[140px] max-h-64 overflow-y-auto">
          {COMPACT_METRICS.map((m) => (
            <button
              key={m.id}
              onClick={() => handleSelect(m.id)}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors flex items-center justify-between ${
                m.id === metricId ? 'bg-zinc-100 dark:bg-zinc-700 font-bold' : ''
              }`}
            >
              <span className="font-bold text-zinc-500 dark:text-zinc-400 w-10">{m.abbr}</span>
              <span className="text-zinc-700 dark:text-zinc-200 flex-1">{m.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Export the metrics list for use elsewhere
export { COMPACT_METRICS };
